/**
 * The kind's sections in the review: the ones the AI filled (with the AI marker and their quote) and the empty
 * suggestions, each a `SectionCard` held in the draft. A section saves only with text. Template sections marked
 * internal start internal.
 */

import { fromExtractionSectionBodyMax } from '@/api/generated/schemas';
import type { SectionAudience } from '@/api/types/assets';
import { SectionCard } from '@/ui/SectionCard';

import type { DraftSection } from './fromFilesDraft';
import { SourceNote, type FileNames } from '../files/SourceNote';

export function ReviewSections({
  sections,
  names,
  onChange,
}: {
  sections: DraftSection[];
  names: FileNames;
  onChange: (key: string, patch: Partial<DraftSection>) => void;
}) {
  const filled = sections.filter((s) => s.source !== null);
  const empty = sections.filter((s) => s.source === null);
  const card = (s: DraftSection) => (
    <SectionCard
      key={s.key}
      title={s.title}
      state={s.body.trim() ? 'filled' : 'suggested'}
      audience={s.audience}
      onAudience={(audience: SectionAudience) => onChange(s.key, { audience })}
      body={s.body}
      onBody={(body) => onChange(s.key, { body })}
      maxLength={fromExtractionSectionBodyMax}
      actions={
        <>
          {s.source && <SourceNote source={s.source} names={names} what={s.title} />}
          {s.body !== '' && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(s.key, { body: '' })}>
              Clear {s.title}
            </button>
          )}
        </>
      }
    />
  );
  return (
    <section className="asset-editor-sections" aria-labelledby="review-sections-title">
      <h2 id="review-sections-title">Sections</h2>
      {filled.length === 0 && <p className="hint">The AI wrote no section the files could back. Write them here or after saving.</p>}
      {filled.map(card)}
      {empty.length > 0 && <h3 className="asset-editor-subhead">Not found in the files</h3>}
      {empty.map(card)}
    </section>
  );
}
